import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useRootStore } from "store";
import WizardStep from "views/wizard/components/WizardStep";
import CustomerInformation from "views/wizard/components/CustomerInformation/CustomerInformation";
import ExternalUsers from "views/wizard/components/ExternalUsers/ExternalUsers";
import InternalUsers from "views/wizard/components/InternalUsers/InternalUsers";
import CustomerService from "views/wizard/components/CustomerService/CustomerService";
import UserService from "views/wizard/components/UserService/UserService";
import InviteExternalUsers from "views/wizard/components/inviteExternalUsers/InviteExternalUsers";
import { captureEvent } from "components/posthog/customEventCapture";
import { Icon } from "components/icon/Icon";
import MKLoader from "components/loader/MKLoader";
import { ExternalUser, ExternalUserRole } from "models/customers/customers";
import Modal from "components/modal/Modal";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";
import { useGlobalContext } from "app/store/GlobalContext";

import "./CustomerSettings.scss";

export default function CustomerSettings() {
  const { editingState, setEditingState, redirectingState } =
    useGlobalContext();

  // Cleanup: reset editing state on component unmount
  useEffect(() => {
    return () => {
      setEditingState({ isEditing: false, location: null });
    };
  }, [setEditingState]);

  const navigate = useNavigate();

  const {
    showWarningFp,
    isSaving,
    customer,
    isLoading,
    hasErrors,
    fetchCustomer,
    updateCustomer,
    addCustomerUserToCustomer,
    cancelUpdateWarning,
    initUserServices,
    removeCustomerUserFromCustomer,
    resetValidUserData,
    resetWizard,
    setFpService,
    setUserFpService,
    toggleInviteAllExternalUsers,
    toggleInviteExternalUser,
    toggleDocumentServices,
    toggleUserDocumentService,
    toggleUserOnManyDocumentServices
  } = useRootStore((state) => state.wizard);
  const { externalUsers, documentServices, fpConfig, disableFp } = customer;
  const { global } = useRootStore((state) => state);
  const [step, setStep] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (global.workingCustomerId) {
      fetchCustomer(global.workingCustomerId);
    }

    return () => {
      resetWizard();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextClick = () => {
    if (step === 0 && !verifyOwnerConstraint()) {
      return;
    }
    setStep(step + 1);
  };

  const handleBackClick = () => {
    setStep(step - 1);
  };

  const handleInitUserServiceConfiguration = () => {
    initUserServices();
  };

  const verifyOwnerConstraint = () => {
    if (
      externalUsers.length > 0 &&
      externalUsers.filter((user) => user.roles.includes(ExternalUserRole.Admin))
        .length === 0
    ) {
      return false;
    }
    return true;
  };

  const handleUpdateClick = () => {
    updateCustomer(navigate);
  };

  const handleToggleChildServices = (ids: string[], enabled: boolean) => {
    toggleDocumentServices(ids, enabled);
  };

  const handleAddCustomerUser = (
    user: Partial<ExternalUser>,
    isNew?: boolean
  ) => {
    addCustomerUserToCustomer(user, user.roles, isNew);
  };

  const handleRemoveCustomerUserFromCustomer = (email: string) => {
    removeCustomerUserFromCustomer(email);
  };

  const handleInviteExternalUser = (email: string, invite: boolean) => {
    toggleInviteExternalUser(email, invite);
  };

  const handleInviteAllExternalUsers = (invite: boolean) => {
    toggleInviteAllExternalUsers(invite);
  };

  const navigateToStep = (newStep: number) => {
    if (step === 0 && !verifyOwnerConstraint()) {
      return;
    }
    setStep(newStep);
  };

  const handleUserServiceToggle = (
    userId: string,
    serviceId: string,
    value: boolean
  ) => {
    toggleUserDocumentService(userId, serviceId);
  };

  const handleChildUserServicesToggle = (
    userId: string,
    serviceIds: string[],
    value: boolean
  ) => {
    toggleUserOnManyDocumentServices(userId, serviceIds, value);
  };

  const handleSetFpService = (customerChoice: string) => {
    setFpService(customerChoice);
  };

  const handleUserSetFpService = (customerChoice: string, userId: string) => {
    setUserFpService(customerChoice, userId);
  };

  const handleCustomerUpdate = () => {
    updateCustomer(navigate);
  };

  const cancelModal = () => {
    setConfirmDelete(false);
    cancelUpdateWarning();
  };

  const handleResetValidUserData = () => {
    resetValidUserData();
  };

  const handleConfirmDelete = () => {
    setConfirmDelete(!confirmDelete);
  };

  const renderText = (header: string, paragraphs: string[]) => {
    const paragraphsToRender: ReactNode[] = [];
    for (let i = 0; i < paragraphs.length; i++) {
      paragraphsToRender.push(<p key={i}>{paragraphs[i]}</p>);
    }
    return [<h1 key="modH">{header}</h1>, ...paragraphsToRender];
  };

  const renderCheckbox = (serviceName: string) => {
    return (
      <div className="modalCheckboxWrapper">
        <input
          id="modalCheck"
          name="modalCheck"
          type="checkbox"
          onChange={handleConfirmDelete}
        />
        <label htmlFor="modalCheck">
          Ja, jag vill avsluta {serviceName} för{" "}
          <strong>{customer.customerName}</strong>
        </label>
      </div>
    );
  };

  const renderButtons = () => {
    return (
      <div className="modalButtons">
        <button
          className="primaryButton"
          type="button"
          disabled={!confirmDelete}
          onClick={() => {
            handleCustomerUpdate();
            captureEvent("Radera");
          }}
        >
          Radera
        </button>
        <button
          className="secondaryButton"
          type="button"
          onClick={() => {
            cancelModal();
            captureEvent("Avbryt");
          }}
        >
          Avbryt
        </button>
      </div>
    );
  };

  const createModal = () => {
    if (showWarningFp) {
      return (
        <Modal onDismiss={cancelModal} modalTitle="warning">
          <div className="modalText">
            {renderText("Viktigt meddelande!", [
              "Du har valt att slå av Resultat & Analys. Detta innebär att Resultat & Analys avslutas och all lagrad data kommer raderas omgående.",
              'Säkerställ att kunden har laddat ner all viktigt data innan du klickar på knappen "Radera".'
            ])}
            {renderCheckbox("Resultat & Analys")}
            {renderButtons()}
          </div>
        </Modal>
      );
    }

    return null;
  };

  const renderWizardSteps = () => {
    const wizardSteps = [
      { title: "1. Uppgifter kund & kundteam", step: 0 },
      { title: "2. Digitala tjänster & dokument", step: 1 },
      { title: "3. Behörigheter & tillgång till tjänster", step: 2 },
      { title: "4. Bekräfta & bjud in", step: 3 }
    ];

    return wizardSteps.map((currentStep) => (
      <li
        key={currentStep.step}
        onClick={() => {
          navigateToStep(currentStep.step);
          captureEvent(currentStep.title);
        }}
        className={step === currentStep.step ? "activeStep" : "step"}
      >
        {currentStep.title}
      </li>
    ));
  };

  const saveButtonText =
    externalUsers.filter((user) => user.id === undefined).length > 0
      ? "Bjud in & uppdatera kund"
      : "Uppdatera kund";

  return (
    <>
      <div className="customerSettings box">
        <ul>{renderWizardSteps()}</ul>
        <div>
          <WizardStep active={step === 0}>
            <>
              <CustomerInformation
                customer={customer}
                collapsed={false}
                info="Kontrollera att kunduppgifterna stämmer. Kunduppgifterna hämtas ifrån MittLudvig och om du behöver ändra något så gör du det i MittLudvig. Kort därefter kommer uppdateringen att synas i MittKontor."
              />
              <ExternalUsers
                users={externalUsers}
                collapsed={false}
                onItemAdded={handleAddCustomerUser}
                onItemRemove={handleRemoveCustomerUserFromCustomer}
                resetValidUserData={handleResetValidUserData}
                info={<span><b>Kundanvändare</b> är personer på kundföretaget som har inloggning till Mitt Kontor. Den tilldelade rollen styr vilka mappar och funktioner användaren får åtkomst till. Läs mer i användarguiden på intranätet.</span>}
              />
              <InternalUsers collapsed={false} />
            </>
          </WizardStep>
          <WizardStep active={step === 1}>
            <CustomerService
              fpConfig={fpConfig}
              onSetFpService={handleSetFpService}
              services={documentServices}
              collapsed={false}
              onToggleChildServices={handleToggleChildServices}
              disableFp={disableFp}
            />
          </WizardStep>
          <WizardStep
            onInit={handleInitUserServiceConfiguration}
            active={step === 2}
          >
            <UserService
              isInternal={false}
              summaryTitle="Tillgång till tjänster: Kundanvändare"
              editTitle="Redigera kundanvändare"
              users={externalUsers}
              collapsed={false}
              fpConfig={fpConfig}
              onSetFpService={handleUserSetFpService}
              onToggleService={handleUserServiceToggle}
              onToggleChildServices={handleChildUserServicesToggle}
              disableFp={disableFp}
            />
          </WizardStep>
          <WizardStep active={step === 3}>
            <>
              <CustomerInformation customer={customer} collapsed={false} />
              <ExternalUsers
                users={externalUsers}
                collapsed
                onItemAdded={handleAddCustomerUser}
                onItemRemove={handleRemoveCustomerUserFromCustomer}
              />
              <InternalUsers collapsed />
              <InviteExternalUsers
                externalUsers={externalUsers}
                onInviteUser={handleInviteExternalUser}
                onInviteAllUsers={handleInviteAllExternalUsers}
                isWizard={false}
              />
            </>
          </WizardStep>
          <div className="buttonContainer">
            <button
              className="backButton"
              onClick={() => {
                handleBackClick();
                captureEvent("Tillbaka");
              }}
              style={{ display: step > 0 ? "block" : "none" }}
            >
              <Icon name={"angle-left"} size="lg" /> Tillbaka
            </button>
            <button
              className="forwardButton"
              onClick={() => {
                handleNextClick();
                captureEvent("Nästa");
              }}
              style={{ display: step < 3 ? "block" : "none" }}
            >
              Nästa <Icon name={"angle-right"} size="lg" />
            </button>
            <button
              className="primaryButton"
              disabled={isLoading || hasErrors}
              onClick={() => {
                handleUpdateClick();
                captureEvent(saveButtonText);
              }}
              style={{ display: step === 3 ? "block" : "none" }}
            >
              {saveButtonText}
            </button>
          </div>
        </div>
        <MKLoader show={isSaving} message="Uppdaterar kund..." />
        {editingState.isEditing &&
          editingState.location === "customer-settings" &&
          redirectingState.isRedirecting && <UnsavedChangesModal />}
      </div>
      {createModal()}
    </>
  );
}